import { logger } from '@/utils/logger';

import { AllowedConnectorTypes } from '../config/allowed-connector-types';
import { AllowedReadFormatTypes, AllowedWriteFormatTypes } from '../config/allowed-format-types';
import { AllowedStatementTypes } from '../config/allowed-statement-types';
import {
  AllowedTaskFunctions,
  AllowedTaskTypePrefixes,
  AllowedTaskTypes,
} from '../config/allowed-task-types';
import dataframes from '../config/dataframe-nodes';
import { generateNodeName } from './generateNodeName';
import { getNodeName } from './getNodeName';
import { isValidNodeName } from './isValidNodeName';

export const nodeUtils = {
  isValidNodeName,
  getNodeName,
  generateNodeName,
  getNodeType,
  getTaskNodeFunction,
  // Main function that updates queries and creates edges
  updateQueriesOnNodeNameChangeAndUpdateEdges: ({
    getNodes,
    getEdges,
    id,
    data,
    updateNodeData,
    updateNodeDataInCurrentContext,
    setEdges,
  }) => {
    try {
      // Properties that may contain queries
      const propertiesWhichAreQueries = ['sqlQuery', 'query', 'queryString'];
      const nodes = getNodes?.();
      const edges = getEdges?.();

      // Get the current node
      const currNode = nodes?.find((n) => n?.id === id);
      if (!currNode) return;

      // Get old name
      let oldName;
      if (nodeUtils?.getNodeType?.(currNode) === 'dataframe') {
        oldName = nodeUtils?.getNodeName?.(currNode?.data?.['data-frame']);
      } else {
        oldName = nodeUtils?.getNodeName?.(currNode?.data?.name);
      }

      // Get new name
      let newName;
      if (nodeUtils?.getNodeType?.(data) === 'dataframe') {
        newName = nodeUtils?.getNodeName?.(data?.['data-frame']);
      } else {
        newName = nodeUtils?.getNodeName?.(data?.name);
      }

      // Skip if names are the same or either is empty/null/undefined
      if (!oldName || !newName || oldName === newName) {
        return;
      }

      // Update references to the node name in other nodes
      nodes?.forEach((node) => {
        if (node?.id !== id) {
          let nodeUpdated = false;

          // Check queries in properties
          propertiesWhichAreQueries?.forEach((property) => {
            if (node?.data?.properties?.[property]) {
              let originalQuery = node?.data?.properties?.[property];
              if (typeof originalQuery === 'object') {
                originalQuery = originalQuery?.['#text'];
              } else {
                originalQuery = originalQuery?.toString();
              }

              // Only update if the old name is actually found in the query
              if (originalQuery?.includes(oldName)) {
                const updatedQuery = originalQuery.replaceAll(oldName, newName);
                if (updatedQuery !== originalQuery) {
                  node.data.properties[property] = updatedQuery;
                  nodeUpdated = true;
                }
              }
            }
          });

          // Check command property
          if (node?.data?.command) {
            const originalCommand = node?.data?.command;
            if (originalCommand.includes(oldName)) {
              const updatedCommand = originalCommand.replaceAll(oldName, newName);
              if (updatedCommand !== originalCommand) {
                node.data.command = updatedCommand;
                nodeUpdated = true;
              }
            }
          }

          // Check query if it's an object with #text
          if (
            node?.data?.query &&
            typeof node?.data?.query === 'object' &&
            node?.data?.query?.['#text']
          ) {
            const originalQueryText = node?.data?.query?.['#text'];
            if (originalQueryText?.includes(oldName)) {
              const updatedQueryText = originalQueryText.replaceAll(oldName, newName);
              if (updatedQueryText !== originalQueryText) {
                node.data.query['#text'] = updatedQueryText;
                nodeUpdated = true;
              }
            }
          }

          // Only update the node if there were actual changes
          if (nodeUpdated) {
            updateNodeData?.(node?.id, node?.data);
            updateNodeDataInCurrentContext?.(node?.data);
          }
        }
      });

      // // Add a delay to ensure all updates are processed
      // setTimeout(() => {
      //   // Get updated nodes and edges
      //   const updatedNodes = getNodes?.();
      //   const updatedEdges = getEdges?.();

      //   // Find nodes that reference other nodes and create edges
      //   createMissingEdges?.(updatedNodes, updatedEdges, setEdges);
      // }, 100);
    } catch (error) {
      logger.error('Error in updateQueriesOnNodeNameChange:', error);
      throw error;
    }
  },
};

function getNodeType(node) {
  const { type } = node || {};
  if (type === undefined) {
    return node?.['@data-type'];
  } else if (AllowedConnectorTypes?.includes?.(type)) {
    return 'connector';
  } else if (AllowedTaskTypes?.includes?.(type)) {
    return 'task';
  } else if (dataframes?.some?.((df) => df?.type === type)) {
    return 'dataframe';
  } else if (AllowedStatementTypes?.includes?.(type?.replace?.('Command', ''))) {
    return 'command';
  } else if ([...AllowedReadFormatTypes, ...AllowedWriteFormatTypes]?.includes?.(type)) {
    return 'format';
  }
  logger.error('Invalid node type:', type);
}

function getTaskNodeFunction(node) {
  const nodeType = getNodeType(node);
  if (nodeType === 'task') {
    const { type } = node;

    // Check if the type is in AllowedTaskTypes
    if (AllowedTaskTypes.includes(type)) {
      // Find which prefix matches
      for (const prefix of AllowedTaskTypePrefixes) {
        if (type.startsWith(prefix)) {
          // Extract function name by removing the prefix
          let functionName = type.substring(prefix.length);

          // Convert first character to lowercase
          functionName = functionName.charAt(0).toLowerCase() + functionName.slice(1);

          // Verify it's in AllowedTaskFunctions
          if (AllowedTaskFunctions.includes(functionName)) {
            return functionName;
          }
          logger.error(`Invalid function name: ${functionName} for node ${node.id}
              Allowed functions are: ${AllowedTaskFunctions.join(', ')}
              `);
        }
      }
    }
  }
  return null;
}
